import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { OrganizationInvitationEmailService } from '../api-services/organization-invitation-email.service';
import { PendingInvitation } from '../data-models/pending-invitation.model';
import { OrganizationInvitationRequest } from '../request-models/organization-invitation-request.model';
import { InvitationEmailResult } from '../response-models/invitation-email-result.model';
import { InvitationListResponse } from '../response-models/invitation-list-response.model';
import { InvitationResendResult } from '../response-models/invitation-resend-result.model';
import { InvitationStats } from '../response-models/invitation-stats.model';
import { adminOrgPrefix } from '../utils/admin-org-path-utils';
import { toInvitationResendResult } from '../utils/invitation-result-utils';
import { isRecord } from '../utils/is-record';
import { countOrZero, omitUndefined } from '../utils/wire-value-utils';

/** Which invitations to list. */
export type InvitationStatusFilter = 'pending' | 'accepted' | 'cancelled' | 'expired' | 'all';

/**
 * An organization's invitations: `/admin/organizations/:id/invitations[/:invitationId[/resend]]` (hub
 * `admin-organization-invitations-controller.ts`). The first email send goes through
 * `/admin/organization-invitations/:id/send-email` (`OrganizationInvitationEmailService`).
 * Failures reject with an `ApiError`.
 */
@Injectable({ providedIn: 'root' })
export class OrganizationInvitationService extends BaseService<PendingInvitation> {
  private readonly invitationEmail = inject(OrganizationInvitationEmailService);

  constructor() {
    super('invitations', PendingInvitation);
  }

  /** The organization's pending invitations and stats. */
  getPendingInvitations(organizationId: string): Promise<InvitationListResponse> {
    return this.getInvitations(organizationId, 'pending');
  }

  /** The organization's invitations (filtered by `status`) and stats. */
  getInvitations(organizationId: string, status: InvitationStatusFilter): Promise<InvitationListResponse> {
    const opts = { prefix: adminOrgPrefix(organizationId), query: { status } };
    return firstValueFrom(this.request('GET', opts, data => this.toList(data)));
  }

  /** POST .../invitations: invites someone; the hub does not email them until `sendInvitationEmail`. */
  inviteMember(organizationId: string, body: OrganizationInvitationRequest): Promise<PendingInvitation | null> {
    return firstValueFrom(this.create(toInvitationRequest(body), { prefix: adminOrgPrefix(organizationId) }));
  }

  /** POST .../invitations/:id/resend */
  resendInvitation(organizationId: string, invitationId: string): Promise<InvitationResendResult> {
    const opts = { prefix: adminOrgPrefix(organizationId), id: invitationId, path: 'resend' };
    return firstValueFrom(this.request('POST', opts, toInvitationResendResult, {}));
  }

  /** DELETE .../invitations/:id: cancels the invitation. */
  async cancelInvitation(organizationId: string, invitationId: string): Promise<void> {
    await firstValueFrom(this.delete(invitationId, { prefix: adminOrgPrefix(organizationId) }));
  }

  /** POST /admin/organization-invitations/:id/send-email */
  sendInvitationEmail(invitationId: string): Promise<InvitationEmailResult> {
    return firstValueFrom(this.invitationEmail.sendEmail(invitationId));
  }

  private toList(data: unknown): InvitationListResponse {
    const source = isRecord(data) ? data : {};
    const invitations = Array.isArray(source['invitations']) ? source['invitations'].map(item => this.createType(item)) : [];
    return { invitations, stats: toInvitationStats(source['stats']) };
  }
}

function toInvitationRequest(body: OrganizationInvitationRequest): OrganizationInvitationRequest {
  return omitUndefined<OrganizationInvitationRequest>({
    email: body.email,
    firstName: body.firstName,
    lastName: body.lastName,
    role: body.role,
  });
}

function toInvitationStats(data: unknown): InvitationStats {
  const source = isRecord(data) ? data : {};
  return {
    total: countOrZero(source['total']),
    pending: countOrZero(source['pending']),
    accepted: countOrZero(source['accepted']),
    cancelled: countOrZero(source['cancelled']),
    expired: countOrZero(source['expired']),
  };
}
